import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  Itinerary,
  ItineraryLatenessDetail,
  ItineraryStopLateness,
} from 'src/common/external/bambi-api/bambiApi';

export type ItineraryStopsByTripLocationType = Partial<
  Record<'pickup' | 'dropoff', ItineraryStopLateness>
>;

export interface ItineraryState {
  stopsByTripId: Record<
    string,
    Record<string, ItineraryStopsByTripLocationType | undefined> | undefined
  >;
  lateTripIdMap: Record<string, Record<string, boolean> | undefined>;
}

export const initialState: ItineraryState = {
  stopsByTripId: {},
  lateTripIdMap: {},
};

export function getStopsByTripId(
  itinerary?: Itinerary,
  itineraryLateness?: ItineraryLatenessDetail
) {
  if (!itinerary || !itineraryLateness) {
    return {};
  }

  return itinerary.stops.reduce<
    Record<string, ItineraryStopsByTripLocationType>
  >((stopsByTripId, stop, i) => {
    const detail = itineraryLateness.stops[i] as
      | ItineraryStopLateness
      | undefined;

    // The lists are mirrored, but just in case...
    if (!detail) {
      return stopsByTripId;
    }

    stop.trips.forEach((trip) => {
      stopsByTripId[trip.id] = {
        [trip.purpose]: detail,
        ...stopsByTripId[trip.id],
      };
    });

    return stopsByTripId;
  }, {});
}

export const itinerarySlice = createSlice({
  name: 'itinerary',
  initialState,
  reducers: {
    reset(state) {
      state.stopsByTripId = initialState.stopsByTripId;
      state.lateTripIdMap = initialState.lateTripIdMap;
    },
    cacheTripItineraryLateness(
      state,
      action: PayloadAction<{
        itinerary: Itinerary;
        itineraryLateness: ItineraryLatenessDetail;
      }>
    ) {
      const { itinerary, itineraryLateness } = action.payload;
      const assignmentId = itinerary.assignment_id;
      state.stopsByTripId = {
        ...state.stopsByTripId,
        [assignmentId]: {
          ...getStopsByTripId(itinerary, itineraryLateness),
        },
      };
      state.lateTripIdMap = {
        ...state.lateTripIdMap,
        [assignmentId]: {
          ...itineraryLateness.late_trip_ids.reduce<Record<string, boolean>>(
            (acc, id) => ({
              [id]: true,
              ...acc,
            }),
            {}
          ),
        },
      };
    },
  },
});

export const { cacheTripItineraryLateness, reset } = itinerarySlice.actions;
